import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { Box, Button, Chip, CircularProgress, useMediaQuery } from "@mui/material";
import EventAvailableOutlinedIcon from "@mui/icons-material/EventAvailableOutlined";
import PlayCircleOutlinedIcon from "@mui/icons-material/PlayCircleOutlined";
import CheckCircleOutlinedIcon from "@mui/icons-material/CheckCircleOutlined";
import VerifiedOutlinedIcon from "@mui/icons-material/VerifiedOutlined";
import StarOutlinedIcon from "@mui/icons-material/StarOutlined";
import ReplayOutlinedIcon from "@mui/icons-material/ReplayOutlined";
import StickyNote2OutlinedIcon from "@mui/icons-material/StickyNote2Outlined";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import UnfoldLessIcon from "@mui/icons-material/UnfoldLess";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import { dt, accentGradient, accentGradientHover } from "../../../../shared/themes/redesignTokens";
import useTranslation from "../../../../shared/hooks/useTranslation";
import useDataRefresh from "../../../../shared/notifications/hooks/useDataRefresh";
import useVisibilityRefresh from "../../../../shared/hooks/useVisibilityRefresh";
import { Offer, OfferStatusCode } from "../../models/models";
import { ServiceCompletionDetails, ProgressUpdate, UpdateType } from "../../../servicing/models/executionModels";
import { ReviewCheck, ServiceReview, CreateReviewRequest } from "../../../servicing/models/reviewModels";
import { MilestoneType } from "../../../servicing/components/rfsDetail/logbookUtils";
import LogbookMilestone from "../../../servicing/components/rfsDetail/LogbookMilestone";
import RfsCompletionCard from "../../../servicing/components/rfsDetail/RfsCompletionCard";
import RfsReviewSection from "../../../servicing/components/rfsDetail/RfsReviewSection";
import ProviderProgressTimeline from "../ProviderProgressTimeline";
import CompleteWorkDialog from "../CompleteWorkDialog";
import ConfirmCompletionDialog from "../../../servicing/components/ConfirmCompletionDialog";
import RequestRevisionDialog from "../../../servicing/components/RequestRevisionDialog";
import LeaveReviewDialog from "../../../servicing/components/LeaveReviewDialog";
import offerExecutionApi from "../../api/offerExecutionApi";
import serviceExecutionApi from "../../../servicing/api/serviceExecutionApi";
import serviceReviewsApi from "../../../servicing/api/serviceReviewsApi";
import {
  buildOfferLogbookEntries,
  LogbookEntry,
  LogbookMilestoneEntry,
  LogbookProgressNote,
  milestoneColorMap,
} from "./offerLogbookUtils";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { formatDateTimeAt } from "../../../../shared/utils/formatDateTime";
import { formatUiDate } from "../../../../shared/utils/dateFormat";

dayjs.extend(relativeTime);

// ===================================================================
//  Props
// ===================================================================
interface OfferLogbookSectionProps {
  offer: Offer;
  isOwner: boolean;
  serviceRequestId?: string;
  onDataRefresh: () => void;
}

// ===================================================================
//  Icon map per milestone type
// ===================================================================
const milestoneIcons: Record<string, React.ReactNode> = {
  serviceConfirmed: <EventAvailableOutlinedIcon sx={{ fontSize: 18 }} />,
  workStarted: <PlayCircleOutlinedIcon sx={{ fontSize: 18 }} />,
  workCompleted: <CheckCircleOutlinedIcon sx={{ fontSize: 18 }} />,
  ownerConfirmed: <VerifiedOutlinedIcon sx={{ fontSize: 18 }} />,
  reviewLeft: <StarOutlinedIcon sx={{ fontSize: 18 }} />,
};

// ===================================================================
//  Component
// ===================================================================
const OfferLogbookSection: React.FC<OfferLogbookSectionProps> = ({
  offer,
  isOwner,
  serviceRequestId,
  onDataRefresh,
}) => {
  const { translate } = useTranslation(["serviceOffers", "servicing", "common"]);
  const isPhone = useMediaQuery("(max-width:768px)");

  // Completion data
  const [completion, setCompletion] = useState<ServiceCompletionDetails | null>(null);
  const [completionFiles, setCompletionFiles] = useState<Array<{ name: string; size: number }>>([]);

  // Progress updates
  const [progressUpdates, setProgressUpdates] = useState<ProgressUpdate[]>([]);

  // Review data
  const [reviewCheck, setReviewCheck] = useState<ReviewCheck | null>(null);
  const [reviews, setReviews] = useState<ServiceReview[]>([]);
  const [reviewDismissed, setReviewDismissed] = useState(false);

  // Dialog states
  const [completeWorkDialogOpen, setCompleteWorkDialogOpen] = useState(false);
  const [confirmCompletionDialogOpen, setConfirmCompletionDialogOpen] = useState(false);
  const [revisionDialogOpen, setRevisionDialogOpen] = useState(false);
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);

  // Action loading
  const [actionLoading, setActionLoading] = useState(false);
  const [reviewSubmitting, setReviewSubmitting] = useState(false);

  // Expand/collapse state
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [allExpanded, setAllExpanded] = useState(false);

  // Jump-to-latest chip visibility
  const [showJumpChip, setShowJumpChip] = useState(false);
  const currentMilestoneRef = useRef<HTMLDivElement>(null);
  const logbookPdfRef = useRef<HTMLDivElement>(null);
  const [pdfExporting, setPdfExporting] = useState(false);

  const statusCode = offer.statusCode;
  const isWorkInProgress = statusCode === OfferStatusCode.workInProgress;
  const isWorkCompleted = statusCode === OfferStatusCode.workCompleted;
  const isServiceCompleted = statusCode === OfferStatusCode.serviceCompleted;
  const isConfirmed = statusCode === OfferStatusCode.confirmed;

  // Only show logbook for execution-phase statuses
  const showLogbook = isConfirmed || isWorkInProgress || isWorkCompleted || isServiceCompleted;

  const hasReviewed = reviewCheck?.hasReviewed || false;

  // ===================================================================
  //  Data fetching
  // ===================================================================
  const fetchAllData = useCallback(() => {
    if (!offer.id) return;

    // Always fetch progress updates
    offerExecutionApi
      .getProgressUpdates(offer.id)
      .then((data) => setProgressUpdates(data))
      .catch(() => setProgressUpdates([]));

    // Fetch completion data when relevant
    if (isWorkCompleted || isServiceCompleted) {
      offerExecutionApi
        .getCompletion(offer.id)
        .then((data) => setCompletion(data))
        .catch(() => setCompletion(null));
    } else {
      setCompletion(null);
    }

    // Fetch completion files for owner confirmation dialog
    if (isOwner && isWorkCompleted && serviceRequestId) {
      serviceExecutionApi
        .getCompletionFiles(serviceRequestId)
        .then((files) => setCompletionFiles(files.map((f) => ({ name: f.name, size: f.size }))))
        .catch(() => setCompletionFiles([]));
    }

    // Fetch review data when service completed
    const srId = serviceRequestId || offer.serviceRequest?.id;
    if (isServiceCompleted && srId) {
      serviceReviewsApi
        .checkHasReviewed(srId)
        .then((check) => setReviewCheck(check))
        .catch(() => setReviewCheck(null));
      serviceReviewsApi
        .getReviewsForServiceRequest(srId)
        .then((r) => setReviews(r))
        .catch(() => setReviews([]));
    }
  }, [offer.id, statusCode, isOwner, isWorkCompleted, isServiceCompleted, serviceRequestId, offer.serviceRequest?.id]);

  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  // ===================================================================
  //  Real-time refresh via SignalR + visibility
  // ===================================================================
  useDataRefresh("myOffers", fetchAllData);
  useDataRefresh("receivedOffers", fetchAllData);
  useDataRefresh("serviceRequest", fetchAllData);
  useVisibilityRefresh(fetchAllData);

  // ===================================================================
  //  Build logbook entries
  // ===================================================================
  const entries = useMemo(
    () =>
      buildOfferLogbookEntries({
        statusCode,
        confirmedByProviderDate: offer.confirmedByProviderDate,
        confirmedArrivalDate: offer.confirmedArrivalDate,
        confirmedArrivalTimeFrom: offer.confirmedArrivalTimeFrom,
        confirmedArrivalTimeTo: offer.confirmedArrivalTimeTo,
        confirmationMessage: offer.confirmationMessage,
        workStartedDate: offer.workStartedDate,
        completion,
        progressUpdates,
        reviews,
        hasReviewed,
      }),
    [statusCode, offer, completion, progressUpdates, reviews, hasReviewed]
  );

  // Intersection observer for "Jump to latest" chip on phone
  useEffect(() => {
    if (!isPhone || !currentMilestoneRef.current) return;
    const observer = new IntersectionObserver(
      ([entry]) => setShowJumpChip(!entry.isIntersecting),
      { threshold: 0.3 }
    );
    observer.observe(currentMilestoneRef.current);
    return () => observer.disconnect();
  }, [isPhone, entries]);

  const scrollToLatest = useCallback(() => {
    currentMilestoneRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "center",
    });
  }, []);

  // Set default expanded state: current milestone expanded
  useEffect(() => {
    if (allExpanded) return;
    const currentEntry = entries.find(
      (e) => e.kind === "milestone" && (e as LogbookMilestoneEntry).status === "current"
    );
    if (currentEntry) {
      setExpandedIds(new Set([currentEntry.id]));
    }
  }, [entries, allExpanded]);

  // ===================================================================
  //  Handlers
  // ===================================================================
  const toggleExpand = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const toggleExpandAll = () => {
    if (allExpanded) {
      const currentEntry = entries.find(
        (e) => e.kind === "milestone" && (e as LogbookMilestoneEntry).status === "current"
      );
      setExpandedIds(currentEntry ? new Set([currentEntry.id]) : new Set());
      setAllExpanded(false);
    } else {
      setExpandedIds(new Set(entries.map((e) => e.id)));
      setAllExpanded(true);
    }
  };

  // Provider: Mark work complete
  const handleMarkWorkComplete = async (data: {
    completionSummary: string;
    providerNotes?: string;
    serviceReportFiles?: File[];
  }) => {
    setActionLoading(true);
    try {
      const files = data.serviceReportFiles
        ? await Promise.all(
            data.serviceReportFiles.map(async (file) => {
              const content = await fileToBase64(file);
              return { name: file.name, content };
            })
          )
        : undefined;

      await offerExecutionApi.markWorkComplete(offer.id, {
        completionSummary: data.completionSummary,
        providerNotes: data.providerNotes,
        files,
      });
      setCompleteWorkDialogOpen(false);
      onDataRefresh();
    } catch (err) {
      console.error("Failed to mark work complete:", err);
    } finally {
      setActionLoading(false);
    }
  };

  // Owner: Confirm completion
  const handleConfirmCompletion = async () => {
    if (!serviceRequestId) return;
    setActionLoading(true);
    try {
      await serviceExecutionApi.confirmCompletion(serviceRequestId);
      setConfirmCompletionDialogOpen(false);
      onDataRefresh();
    } catch (err) {
      console.error("Failed to confirm completion:", err);
    } finally {
      setActionLoading(false);
    }
  };

  // Owner: Request revision
  const handleRequestRevision = async (reason: string) => {
    if (!serviceRequestId) return;
    setActionLoading(true);
    try {
      await serviceExecutionApi.requestRevision(serviceRequestId, { reason });
      setRevisionDialogOpen(false);
      onDataRefresh();
    } catch (err) {
      console.error("Failed to request revision:", err);
    } finally {
      setActionLoading(false);
    }
  };

  // Owner only: Submit review (BUG-2 fix: only owner can leave reviews)
  const handleSubmitReview = async (rating: number, title?: string, comment?: string) => {
    const srId = serviceRequestId || offer.serviceRequest?.id;
    if (!srId) return;
    setReviewSubmitting(true);
    try {
      const data: CreateReviewRequest = {
        serviceRequestId: srId,
        offerId: offer.id,
        rating,
        title,
        comment,
      };
      await serviceReviewsApi.createReview(data);
      setReviewDialogOpen(false);
      const check = await serviceReviewsApi.checkHasReviewed(srId);
      setReviewCheck(check);
      const updatedReviews = await serviceReviewsApi.getReviewsForServiceRequest(srId);
      setReviews(updatedReviews);
      onDataRefresh();
    } catch (err) {
      console.error("Failed to submit review:", err);
    } finally {
      setReviewSubmitting(false);
    }
  };

  // ===================================================================
  //  PDF Export
  // ===================================================================
  const handleExportPdf = useCallback(async () => {
    const el = logbookPdfRef.current;
    if (!el) return;
    setPdfExporting(true);
    try {
      const html2canvas = (await import("html2canvas")).default;
      const { jsPDF } = await import("jspdf");

      // Temporarily expand all for full snapshot
      const prevExpanded = new Set(expandedIds);
      const prevAllExpanded = allExpanded;
      setExpandedIds(new Set(entries.filter(e => e.kind === "milestone" && (e as LogbookMilestoneEntry).status !== "pending").map(e => e.id)));
      setAllExpanded(true);

      await new Promise(r => setTimeout(r, 350));

      const canvas = await html2canvas(el, {
        scale: 2,
        useCORS: true,
        backgroundColor: "#FFFFFF",
        logging: false,
      });

      setExpandedIds(prevExpanded);
      setAllExpanded(prevAllExpanded);

      const imgData = canvas.toDataURL("image/png");
      const imgWidth = 190;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      const pdf = new jsPDF("p", "mm", "a4");

      pdf.setFontSize(16);
      pdf.setFont("helvetica", "bold");
      pdf.text("Service Logbook", 10, 15);
      pdf.setFontSize(10);
      pdf.setFont("helvetica", "normal");
      pdf.text(`Exported: ${formatDateTimeAt(new Date())}`, 10, 22);
      pdf.text(`Offer: ${offer.internalNumber}`, 10, 28);

      const startY = 34;
      const pageHeight = 297;
      const availableHeight = pageHeight - startY - 10;

      if (imgHeight <= availableHeight) {
        pdf.addImage(imgData, "PNG", 10, startY, imgWidth, imgHeight);
      } else {
        let yOffset = 0;
        let page = 0;
        const sliceHeight = Math.floor((availableHeight / imgHeight) * canvas.height);
        while (yOffset < canvas.height) {
          if (page > 0) pdf.addPage();
          const pageCanvas = document.createElement("canvas");
          pageCanvas.width = canvas.width;
          const remaining = canvas.height - yOffset;
          pageCanvas.height = Math.min(sliceHeight, remaining);
          const ctx = pageCanvas.getContext("2d");
          if (ctx) {
            ctx.drawImage(canvas, 0, yOffset, canvas.width, pageCanvas.height, 0, 0, canvas.width, pageCanvas.height);
          }
          const sliceData = pageCanvas.toDataURL("image/png");
          const sliceImgHeight = (pageCanvas.height * imgWidth) / canvas.width;
          pdf.addImage(sliceData, "PNG", 10, page === 0 ? startY : 10, imgWidth, sliceImgHeight);
          yOffset += pageCanvas.height;
          page++;
        }
      }

      pdf.save(`offer-logbook-${offer.internalNumber}-${dayjs().format("YYYY-MM-DD")}.pdf`);
    } catch (err) {
      console.error("PDF export failed:", err);
    } finally {
      setPdfExporting(false);
    }
  }, [entries, expandedIds, allExpanded, offer.internalNumber]);

  // ===================================================================
  //  Milestone title lookup
  // ===================================================================
  const getMilestoneTitle = (type: MilestoneType): string => {
    const key = `servicing:rfsDetail.logbook.milestone.${type}`;
    return translate(key);
  };

  // ===================================================================
  //  TruncatedText helper
  // ===================================================================
  const TruncatedText = ({ text, lines = 3 }: { text: string; lines?: number }) => {
    const [showFull, setShowFull] = useState(false);
    return (
      <Box>
        <Box
          sx={{
            fontSize: "13px",
            fontFamily: dt.font,
            fontWeight: 400,
            lineHeight: 1.6,
            color: "#374151",
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
            ...(!showFull && {
              display: "-webkit-box",
              WebkitLineClamp: lines,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }),
          }}
        >
          {text}
        </Box>
        {text.length > 200 && (
          <Box
            component="button"
            onClick={() => setShowFull(!showFull)}
            sx={{
              background: "none",
              border: "none",
              padding: 0,
              cursor: "pointer",
              fontSize: "12px",
              fontWeight: 600,
              fontFamily: dt.font,
              color: dt.blue,
              mt: 0.5,
              "&:hover": { textDecoration: "underline" },
            }}
          >
            {showFull
              ? translate("servicing:rfsDetail.showLess")
              : translate("servicing:rfsDetail.showMore")}
          </Box>
        )}
      </Box>
    );
  };

  // ===================================================================
  //  Render milestone content per type
  // ===================================================================
  const renderMilestoneContent = (entry: LogbookMilestoneEntry): React.ReactNode => {
    switch (entry.type) {
      case "serviceConfirmed":
        return renderServiceConfirmedContent(entry);
      case "workStarted":
        return renderWorkStartedContent(entry);
      case "workCompleted":
        return renderWorkCompletedContent(entry);
      case "ownerConfirmed":
        return null; // Timestamp in header is sufficient
      case "reviewLeft":
        return renderReviewContent(entry);
      default:
        return null;
    }
  };

  // --- Service Confirmed: arrival date + time + message ---
  const renderServiceConfirmedContent = (entry: LogbookMilestoneEntry) => {
    const { confirmedArrivalDate, confirmedArrivalTimeFrom, confirmedArrivalTimeTo, confirmationMessage } = entry.data;
    if (!confirmedArrivalDate && !confirmationMessage) return null;
    return (
      <Box
        sx={{
          backgroundColor: "#ECFDF5",
          border: "1px solid #A7F3D0",
          borderRadius: dt.radiusSm,
          p: isPhone ? 1.5 : 2,
          display: "flex",
          flexDirection: "column",
          gap: 1,
        }}
      >
        {confirmedArrivalDate && (
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
            <DetailPill
              label={translate("servicing:arrivalDate")}
              value={formatUiDate(confirmedArrivalDate, "weekdayDate")}
            />
            {confirmedArrivalTimeFrom && confirmedArrivalTimeTo && (
              <DetailPill
                label={translate("servicing:arrivalTime")}
                value={`${confirmedArrivalTimeFrom} - ${confirmedArrivalTimeTo}`}
              />
            )}
          </Box>
        )}
        {confirmationMessage && (
          <Box
            sx={{
              backgroundColor: "#FFF",
              borderRadius: "6px",
              borderLeft: "3px solid #10B981",
              px: 1.5,
              py: 1,
            }}
          >
            <TruncatedText text={confirmationMessage} />
          </Box>
        )}
      </Box>
    );
  };

  // --- Work Started: provider can post updates here, owner sees read-only ---
  const renderWorkStartedContent = (entry: LogbookMilestoneEntry) => {
    if (entry.status === "pending") return null;

    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
        {/* Provider: can post updates */}
        {!isOwner && entry.status === "current" && (
          <ProviderProgressTimeline offerId={offer.id} />
        )}

        {/* Provider: Complete Work button */}
        {!isOwner && entry.status === "current" && (
          <Box sx={{ display: "flex", justifyContent: "center", pt: 1 }}>
            <Button
              variant="contained"
              onClick={() => setCompleteWorkDialogOpen(true)}
              sx={{
                textTransform: "none",
                fontFamily: dt.font,
                fontWeight: 600,
                fontSize: "14px",
                backgroundColor: "#3B82F6",
                borderRadius: dt.radiusSm,
                px: 3,
                py: 1,
                "&:hover": { backgroundColor: "#2563EB" },
              }}
            >
              {translate("serviceOffers:execution.markCompleteTitle")}
            </Button>
          </Box>
        )}

        {/* Timestamp info when completed */}
        {entry.status === "completed" && entry.timestamp && (
          <Box sx={{
            backgroundColor: "#F0F9FF",
            border: "1px solid #BAE6FD",
            borderRadius: dt.radiusSm,
            px: isPhone ? 1.5 : 2,
            py: 1.25,
            fontSize: "13px",
            fontFamily: dt.font,
            color: "#374151",
            lineHeight: 1.6,
          }}>
            {translate("servicing:rfsDetail.logbook.startedWorkAt")} {formatDateTimeAt(entry.timestamp, true)}
          </Box>
        )}
      </Box>
    );
  };

  // --- Work Completed: completion card + owner action buttons ---
  const renderWorkCompletedContent = (entry: LogbookMilestoneEntry) => {
    const srId = serviceRequestId || offer.serviceRequest?.id;
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
        {completion && srId && (
          <RfsCompletionCard
            completion={completion}
            serviceRequestId={srId}
            files={completionFiles}
            embedded
          />
        )}
        {/* Owner: Confirm / Revise buttons (only when work is completed = current milestone) */}
        {isOwner && entry.status === "current" && completion && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              gap: isPhone ? 1.5 : 2,
              flexDirection: isPhone ? "column" : "row",
            }}
          >
            <Button
              variant="contained"
              startIcon={<CheckCircleOutlinedIcon sx={{ fontSize: "18px !important" }} />}
              onClick={() => setConfirmCompletionDialogOpen(true)}
              sx={{
                background: "linear-gradient(135deg, #10B981, #059669)",
                borderRadius: dt.radiusPill,
                textTransform: "none",
                fontWeight: 700,
                fontFamily: dt.font,
                fontSize: "14px",
                px: 3.5,
                py: 1.25,
                boxShadow: "0 2px 8px rgba(16, 185, 129, 0.25)",
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                "&:hover": {
                  background: "linear-gradient(135deg, #059669, #10B981)",
                  transform: "translateY(-2px)",
                  boxShadow: "0 6px 20px rgba(16, 185, 129, 0.3)",
                },
              }}
            >
              {translate("servicing:completion.confirmCompletion")}
            </Button>
            <Button
              variant="outlined"
              startIcon={<ReplayOutlinedIcon sx={{ fontSize: "18px !important" }} />}
              onClick={() => setRevisionDialogOpen(true)}
              sx={{
                borderColor: "#F59E0B",
                color: "#D97706",
                borderRadius: dt.radiusPill,
                textTransform: "none",
                fontWeight: 700,
                fontFamily: dt.font,
                fontSize: "14px",
                px: 3,
                py: 1.1,
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                "&:hover": {
                  borderColor: "#D97706",
                  backgroundColor: "rgba(245, 158, 11, 0.06)",
                  transform: "translateY(-2px)",
                  boxShadow: "0 4px 12px rgba(245, 158, 11, 0.15)",
                },
              }}
            >
              {translate("servicing:completion.requestRevision")}
            </Button>
          </Box>
        )}
        {/* Provider: Waiting indicator when work completed */}
        {!isOwner && entry.status === "current" && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              p: 2,
              backgroundColor: "#FFF8E1",
              borderLeft: "3px solid #FFC107",
              borderRadius: "4px",
            }}
          >
            <Box
              sx={{
                fontSize: "13px",
                color: "#F57F17",
                fontWeight: 500,
                fontFamily: dt.font,
              }}
            >
              {translate("serviceOffers:execution.waitingForConfirmation")}
            </Box>
          </Box>
        )}
      </Box>
    );
  };

  // --- Review: RfsReviewSection for owner, read-only review cards for provider ---
  const renderReviewContent = (entry: LogbookMilestoneEntry) => {
    // BUG-2 fix: Only the owner can leave reviews
    if (isOwner) {
      return (
        <RfsReviewSection
          reviews={reviews}
          hasReviewed={hasReviewed}
          reviewDismissed={reviewDismissed}
          onLeaveReview={() => setReviewDialogOpen(true)}
          onDismissPrompt={() => setReviewDismissed(true)}
        />
      );
    }

    // Provider: read-only view of reviews (no review prompt)
    if (reviews.length > 0) {
      return (
        <RfsReviewSection
          reviews={reviews}
          hasReviewed={true}
          reviewDismissed={true}
          onLeaveReview={() => {}}
          onDismissPrompt={() => {}}
        />
      );
    }

    return null;
  };

  // ===================================================================
  //  Render progress note (minor entry)
  // ===================================================================
  const renderProgressNote = (note: LogbookProgressNote, isLast: boolean) => (
    <LogbookMilestone
      key={note.id}
      type="progressNote"
      title={note.authorName}
      timestamp={note.timestamp}
      status="completed"
      color="#0EA5E9"
      icon={<StickyNote2OutlinedIcon sx={{ fontSize: 14 }} />}
      expanded={expandedIds.has(note.id)}
      onToggle={() => toggleExpand(note.id)}
      isLast={isLast}
      isMinor
    >
      <Box
        sx={{
          backgroundColor: "#F0F7FF",
          border: `1px solid ${dt.cardBorder}`,
          borderLeft: "3px solid #0EA5E9",
          borderRadius: dt.radiusSm,
          px: isPhone ? 1.5 : 2,
          py: 1.25,
        }}
      >
        <TruncatedText text={note.message} />
      </Box>
    </LogbookMilestone>
  );

  // ===================================================================
  //  Don't render if not in execution phase
  // ===================================================================
  if (!showLogbook) return null;

  // Determine the reviewee company name for the review dialog
  // When isOwner=true, offer.customer.name = provider name (due to normalisation in OfferDetailPage)
  // When isOwner=false, offer.customer.name = customer name
  // BUG-2 fix: Only the owner can leave reviews, so the reviewee is always the provider
  const revieweeCompanyName = offer.customer?.name || translate("partnerHub:card.serviceProvider");

  return (
    <>
      <Box
        sx={{
          backgroundColor: dt.cardBg,
          border: `1px solid ${dt.cardBorder}`,
          borderRadius: dt.radius,
          padding: "20px",
          animation: "sectionReveal 0.5s ease-out 0.3s both",
        }}
      >
        {/* Section header with Expand/Collapse toggle + PDF export */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 1.5,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <MenuBookOutlinedIcon sx={{ fontSize: 18, color: dt.textMuted }} />
            <Box
              sx={{
                fontSize: "11px",
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: "1.2px",
                color: dt.textMuted,
                fontFamily: dt.font,
              }}
            >
              {translate("serviceOffers:offerDetail.logbookTitle")}
            </Box>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            {/* PDF Export button */}
            <Box
              component="button"
              onClick={handleExportPdf}
              disabled={pdfExporting}
              sx={{
                background: "none",
                border: "none",
                padding: "2px 8px",
                cursor: pdfExporting ? "wait" : "pointer",
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                fontSize: "11px",
                fontWeight: 600,
                fontFamily: dt.font,
                color: dt.textSecondary,
                borderRadius: dt.radiusSm,
                transition: "all 0.15s ease",
                opacity: pdfExporting ? 0.6 : 1,
                "&:hover": {
                  backgroundColor: dt.blueGlow,
                  color: dt.blue,
                },
              }}
            >
              {pdfExporting ? (
                <CircularProgress size={12} sx={{ color: dt.textSecondary }} />
              ) : (
                <PictureAsPdfOutlinedIcon sx={{ fontSize: 14 }} />
              )}
              {translate("servicing:rfsDetail.logbook.exportPdf")}
            </Box>
            {/* Expand/Collapse toggle */}
            {entries.length > 1 && (
              <Box
                component="button"
                onClick={toggleExpandAll}
                sx={{
                  background: "none",
                  border: "none",
                  padding: "2px 8px",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  fontSize: "11px",
                  fontWeight: 600,
                  fontFamily: dt.font,
                  color: dt.textSecondary,
                  borderRadius: dt.radiusSm,
                  transition: "all 0.15s ease",
                  "&:hover": {
                    backgroundColor: dt.blueGlow,
                    color: dt.blue,
                  },
                }}
              >
                {allExpanded ? (
                  <>
                    <UnfoldLessIcon sx={{ fontSize: 14 }} />
                    {translate("servicing:rfsDetail.logbook.collapseAll")}
                  </>
                ) : (
                  <>
                    <UnfoldMoreIcon sx={{ fontSize: 14 }} />
                    {translate("servicing:rfsDetail.logbook.expandAll")}
                  </>
                )}
              </Box>
            )}
          </Box>
        </Box>

        {/* Timeline entries */}
        <Box sx={{ position: "relative" }} ref={logbookPdfRef}>
          {entries.map((entry, index) => {
            const isLast = index === entries.length - 1;

            if (entry.kind === "progressNote") {
              return renderProgressNote(entry as LogbookProgressNote, isLast);
            }

            const ms = entry as LogbookMilestoneEntry;
            const isCurrent = ms.status === "current";
            return (
              <LogbookMilestone
                key={ms.id}
                type={ms.type}
                title={getMilestoneTitle(ms.type as MilestoneType)}
                timestamp={ms.timestamp}
                status={ms.status}
                color={milestoneColorMap[ms.type as MilestoneType]}
                icon={milestoneIcons[ms.type] || <CheckCircleOutlinedIcon sx={{ fontSize: 18 }} />}
                expanded={expandedIds.has(ms.id)}
                onToggle={() => toggleExpand(ms.id)}
                isLast={isLast}
                nodeRef={isCurrent ? currentMilestoneRef : undefined}
              >
                {ms.status !== "pending" ? renderMilestoneContent(ms) : null}
              </LogbookMilestone>
            );
          })}
        </Box>

        {/* "Jump to latest" sticky chip - phone only */}
        {isPhone && showJumpChip && (
          <Box
            sx={{
              position: "sticky",
              bottom: 16,
              display: "flex",
              justifyContent: "center",
              zIndex: 10,
              pointerEvents: "none",
            }}
          >
            <Chip
              label={translate("servicing:rfsDetail.logbook.jumpToLatest")}
              icon={<KeyboardArrowDownIcon sx={{ fontSize: "18px !important" }} />}
              onClick={scrollToLatest}
              sx={{
                pointerEvents: "auto",
                fontFamily: dt.font,
                fontWeight: 700,
                fontSize: "12px",
                background: accentGradient,
                color: "#FFF",
                borderRadius: dt.radiusPill,
                boxShadow: "0 4px 16px rgba(37, 99, 235, 0.3)",
                transition: "all 0.2s ease",
                cursor: "pointer",
                "& .MuiChip-icon": { color: "#FFF" },
                "&:hover": {
                  background: accentGradientHover,
                  transform: "translateY(-2px)",
                  boxShadow: "0 6px 20px rgba(37, 99, 235, 0.4)",
                },
              }}
            />
          </Box>
        )}
      </Box>

      {/* ── Dialogs ── */}

      {/* Provider: Complete Work Dialog */}
      {!isOwner && (
        <CompleteWorkDialog
          open={completeWorkDialogOpen}
          offerNumber={offer.internalNumber}
          onComplete={handleMarkWorkComplete}
          onClose={() => setCompleteWorkDialogOpen(false)}
          loading={actionLoading}
        />
      )}

      {/* Owner: Confirm Completion Dialog */}
      {isOwner && (
        <ConfirmCompletionDialog
          open={confirmCompletionDialogOpen}
          completion={completion}
          files={completionFiles}
          onConfirm={handleConfirmCompletion}
          onRequestRevision={() => {
            setConfirmCompletionDialogOpen(false);
            setRevisionDialogOpen(true);
          }}
          onClose={() => setConfirmCompletionDialogOpen(false)}
          loading={actionLoading}
        />
      )}

      {/* Owner: Request Revision Dialog */}
      {isOwner && (
        <RequestRevisionDialog
          open={revisionDialogOpen}
          onSubmit={handleRequestRevision}
          onClose={() => setRevisionDialogOpen(false)}
          loading={actionLoading}
        />
      )}

      {/* BUG-2 fix: Leave Review Dialog — OWNER ONLY */}
      {isOwner && (
        <LeaveReviewDialog
          open={reviewDialogOpen}
          revieweeCompanyName={revieweeCompanyName}
          onSubmit={handleSubmitReview}
          onSkip={() => {
            setReviewDialogOpen(false);
            setReviewDismissed(true);
          }}
          onClose={() => setReviewDialogOpen(false)}
          loading={reviewSubmitting}
        />
      )}
    </>
  );
};

// ===================================================================
//  Small sub-components
// ===================================================================

const DetailPill = ({ label, value }: { label: string; value: string }) => (
  <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
    <Box
      sx={{
        fontSize: "10px",
        fontWeight: 600,
        fontFamily: dt.font,
        textTransform: "uppercase",
        letterSpacing: "0.5px",
        color: "#6B7280",
      }}
    >
      {label}
    </Box>
    <Box
      sx={{
        fontSize: "13px",
        fontWeight: 600,
        fontFamily: dt.font,
        color: "#1E293B",
      }}
    >
      {value}
    </Box>
  </Box>
);

// ===================================================================
//  Utility
// ===================================================================

/** Convert a File to base64 string */
function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      const base64 = result.split(",")[1] || result;
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export default OfferLogbookSection;
